import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from 'context/ToastContext';
import { pluginApi } from 'services/api';
import { fetchSurveys } from 'state/modules/surveys/actions';
import {
  getAnswers,
  getAvailableSurveys,
} from 'state/modules/surveys/selectors';
import saveSurveyAnswers from 'services/surveys/saveSurveyAnswers';
import validateRequiredQuestions from './Validators/validateRequiredQuestions';
import { buildRequestPayload } from './utils';
import QuestionType from 'components/Surveys/QuestionType';
import RequiredQuestionsModal from 'components/Surveys/RequiredQuestionsModal';
import { Modal, Container, Form, Button, ActionsWrapper } from './style';
import { QuestionsData, SurveysDataForm } from 'components/Surveys/types';
import { useAuth } from 'context/AuthContext';

interface Props {
  isOpen: boolean;
  onRequestClose(): void;
}

const SurveysModal: React.FC<Props> = ({ isOpen, onRequestClose }) => {
  const dispatch = useDispatch();
  const { addToast } = useToast();

  const answerList = useSelector(getAnswers);
  const availableSurveys = useSelector(getAvailableSurveys);
  const { simulating, isLoggedFromAdmin } = useAuth();
  const [survey, setSurvey] = useState<SurveysDataForm>({} as SurveysDataForm);
  const [questions, setQuestions] = useState<QuestionsData[]>([]);
  const [thankYouMessage, setThankYouMessage] = useState('');
  const [requiredQuestions, setRequiredQuestions] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [forceOpen, setForceOpen] = useState(true);

  useEffect(() => {
    dispatch(fetchSurveys());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedFromAdmin) return;
    if (simulating) return;
    async function fetchSurveyData() {
      const filterSurveys = availableSurveys.filter(item => {
        return item.surveys_nps === 1;
      }); // Filtra as pesquisas NPS
      const surveyId = filterSurveys[0]?.id; // Obtém o ID da pesquisa

      // Verifica se já existe dados da pesquisa no localStorage
      if(!surveyId) {
        if(localStorage.getItem('surveyDataExists')) {
          localStorage.removeItem('surveyDataExists');
        }
        return;
      }

      const response = await pluginApi.get(
        `participants/surveys/getSurveyById?survey_id=${surveyId}`,
      );

      if (response.data.data) {
        localStorage.setItem('surveyDataExists', 'true');
      }

      response.data.data.survey_questions.forEach((question: QuestionsData) => {
        question.survey_question_answers.forEach(answer => {
          answer.survey_participant_answers = [];
        });
      });

      setSurvey(response.data.data);
      setQuestions(response.data.data.survey_questions);
      setThankYouMessage(response.data.data.thank_you_message);
    }
    fetchSurveyData();
  }, [availableSurveys, simulating, isLoggedFromAdmin]);

  const handleSave = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const { valid, questions: required } = validateRequiredQuestions(
        answerList,
        questions,
      );

      if (!valid) {
        setRequiredQuestions(required ?? []);
        setIsModalOpen(true);
        return;
      }

      try {
        const filterSurveys = availableSurveys.filter(item => {
          return item.surveys_nps === 1;
        });
        const surveyId = filterSurveys[0]?.id; // Obtém o ID da pesquisa
        const formData = buildRequestPayload(answerList);

        await saveSurveyAnswers({ surveyId, formData });
        setForceOpen(false);
        onRequestClose(); // Chama a função para fechar o modal no componente pai        
        localStorage.removeItem('surveyDataExists');
        addToast({
          title: thankYouMessage || `Obrigado por participar da pesquisa!`,
          type: 'success',
        });

        // Dispatch após o save para atualizar o estado global
        dispatch(fetchSurveys()); // Atualiza a lista de pesquisas disponíveis
      } catch (err) {
        addToast({
          title:
            err.response?.data?.message ||
            'Falha ao enviar respostas. Por favor tente novamente',
          type: 'error',
        });
      }
    },
    [
      addToast,
      answerList,
      questions,
      thankYouMessage,
      availableSurveys,
      onRequestClose,
      dispatch, // Adicionado como dependência
    ],
  );

  return (
    <>
      {survey?.id && (
        <Modal isOpen={isOpen} forceOpen={forceOpen}>
          <Container>
            {survey.banner_picture && survey.banner_picture.trim() !== '' && (
              <img src={survey.banner_picture} alt="banner da pesquisa" />
            )}
            <h1>{survey.title}</h1>
            <Form onSubmit={handleSave}>
              {questions.map(question => (
                <QuestionType
                  key={question.id}
                  type={Number(question.type)}
                  name={question.name}
                  question={question.question}
                  answers={question.survey_question_answers}
                  questioId={question.id}
                  answerId={question.survey_question_answers[0]?.id}
                  scaleType={question.scale_type}
                  alternativeGroups={question.alternative_groups}
                  alreadyAnswered={survey.already_answered}
                  canEdit
                />
              ))}
              <ActionsWrapper>
                <Button type="submit">Salvar</Button>
              </ActionsWrapper>
            </Form>

            <RequiredQuestionsModal
              isOpen={isModalOpen}
              onRequestClose={() => setIsModalOpen(false)}
              text="As questões listadas abaixo são obrigatórias!"
              questions={requiredQuestions}
            />
          </Container>
        </Modal>
      )}
    </>
  );
};

export default SurveysModal;
